import { type Classroom, type DailyMemoAction, dailyMemos, type Student, studentNumberLabel } from "@classroom/domain";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { i18n, useTranslation } from "./i18n";
import { useSaveBeforeUnload } from "./lib/useSaveBeforeUnload";
import { navigationChoice, useNavigationState } from "./lib/workspaceNavigation";
import StudentPagination, { useStudentPagination } from "./StudentPagination";
import ToolPageHeader, { ToolPageNavigation } from "./ToolPageHeader";

export default function HomeroomAffairs({
  sessionStatus,
  readOnly = false,
  classItem,
  students,
  onMemoAction,
  showAffairs,
  children,
}: {
  sessionStatus?: ReactNode;
  readOnly?: boolean;
  classItem: Classroom;
  students: Student[];
  onMemoAction: (action: DailyMemoAction) => Promise<void>;
  showAffairs: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation("dailyMemos");
  const [tab, setTab] = useNavigationState(
    `homeroom:${classItem.id}:tab`,
    showAffairs ? "affairs" : "memos",
    navigationChoice(["memos", "affairs"]),
  );
  useEffect(() => {
    if (showAffairs) setTab("affairs");
  }, [showAffairs, setTab]);
  return (
    <div className="homeroom-affairs-page">
      <ToolPageHeader
        title={i18n.t("marketing:homeroomTab")}
        sessionStatus={sessionStatus}
        navigation={
          <ToolPageNavigation
            label={t("tabs")}
            onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const next =
                event.key === "Home"
                  ? "memos"
                  : event.key === "End"
                    ? "affairs"
                    : tab === "memos"
                      ? "affairs"
                      : "memos";
              setTab(next);
              event.currentTarget.querySelector<HTMLButtonElement>(`#homeroom-${next}-tab`)?.focus();
            }}
          >
            {(["memos", "affairs"] as const).map((key) => (
              <button
                key={key}
                type="button"
                id={`homeroom-${key}-tab`}
                role="tab"
                aria-selected={tab === key}
                aria-controls={`homeroom-${key}-panel`}
                tabIndex={tab === key ? 0 : -1}
                className={tab === key ? "active" : ""}
                onClick={() => setTab(key)}
              >
                {t(key)}
              </button>
            ))}
          </ToolPageNavigation>
        }
      />
      <div role="tabpanel" id="homeroom-memos-panel" aria-labelledby="homeroom-memos-tab" hidden={tab !== "memos"}>
        <DailyMemosPanel readOnly={readOnly} classItem={classItem} students={students} onMemoAction={onMemoAction} />
      </div>
      <div
        role="tabpanel"
        id="homeroom-affairs-panel"
        aria-labelledby="homeroom-affairs-tab"
        hidden={tab !== "affairs"}
      >
        {children}
      </div>
    </div>
  );
}

function DailyMemosPanel({
  readOnly = false,
  classItem,
  students,
  onMemoAction,
}: {
  readOnly?: boolean;
  classItem: Classroom;
  students: Student[];
  onMemoAction: (action: DailyMemoAction) => Promise<void>;
}) {
  const { t } = useTranslation("dailyMemos");
  const [title, setTitle] = useState("");
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  useSaveBeforeUnload(busy);
  const [error, setError] = useState("");
  const memos = dailyMemos(classItem);
  const pagination = useStudentPagination(
    students.filter((student) => student.active),
    `${classItem.id}:${editingId}`,
  );

  async function save(action: DailyMemoAction) {
    // Keep cards focusable during autosave; block repeated activation here.
    if (busyRef.current || readOnly) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await onMemoAction(action);
      if (action.type === "create") setTitle("");
      if (action.type === "delete" && action.id === editingId) setEditingId("");
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "";
      setError(code === "duplicate-memo" ? t("duplicate") : code === "stale-memo" ? t("stale") : t("failed"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  return (
    <section
      className="paper-card daily-memos"
      aria-labelledby="daily-memos-title"
      aria-busy={busy}
      data-autosave
      data-save-state={busy ? "saving" : "saved"}
    >
      <h2 id="daily-memos-title" className="sr-only">
        {t("memos")}
      </h2>
      <div className="daily-memo-list">
        {memos.map((memo) => {
          const selected = students.filter((student) => memo.studentIds.includes(student.id));
          const editing = editingId === memo.id;
          return (
            <section className="daily-memo" key={memo.id} aria-labelledby={`daily-memo-title-${memo.id}`}>
              <header className="daily-memo-heading">
                <div className="daily-memo-name">
                  <h3 id={`daily-memo-title-${memo.id}`} className="daily-memo-title">
                    {memo.title}
                  </h3>
                  <span className="daily-memo-count">{t("count", { count: selected.length })}</span>
                </div>
                <div className="daily-memo-actions">
                  <button
                    type="button"
                    disabled={readOnly}
                    aria-disabled={busy || readOnly}
                    aria-expanded={editing}
                    aria-controls={`daily-memo-students-${memo.id}`}
                    onClick={() => {
                      if (!busyRef.current) setEditingId(editing ? "" : memo.id);
                    }}
                  >
                    {t(editing ? "done" : "edit")}
                  </button>
                  <button
                    type="button"
                    className="daily-memo-delete"
                    disabled={readOnly}
                    aria-disabled={busy || readOnly}
                    aria-label={t("deleteLabel", { title: memo.title })}
                    onClick={() => {
                      if (busyRef.current) return;
                      if (window.confirm(t("deleteConfirm", { title: memo.title })))
                        void save({ type: "delete", id: memo.id });
                    }}
                  >
                    {t("delete")}
                  </button>
                </div>
              </header>
              <div className="daily-memo-selected">
                {selected.length ? (
                  selected.map((student) => (
                    <span className="daily-memo-student" key={student.id}>
                      {studentNumberLabel(student)} {student.name}
                    </span>
                  ))
                ) : (
                  <span className="daily-memo-empty">{t("noSelected")}</span>
                )}
              </div>
              <div id={`daily-memo-students-${memo.id}`} hidden={!editing}>
                <div className="routine-student-grid daily-memo-student-grid">
                  {pagination.items.map((student) => {
                    const checked = memo.studentIds.includes(student.id);
                    return (
                      <button
                        type="button"
                        key={student.id}
                        disabled={readOnly}
                        aria-disabled={busy || readOnly}
                        className={checked ? "selected" : ""}
                        aria-pressed={checked}
                        aria-label={t(checked ? "unselectStudent" : "selectStudent", {
                          number: studentNumberLabel(student),
                          name: student.name,
                        })}
                        onClick={() =>
                          void save({ type: "setStudent", id: memo.id, studentId: student.id, selected: !checked })
                        }
                      >
                        <span data-source-class={student.sourceClass}>{studentNumberLabel(student, true)}</span>
                        <b>{student.name}</b>
                        <small>{t(checked ? "selected" : "unselected")}</small>
                      </button>
                    );
                  })}
                </div>
                {!students.some((student) => student.active) && <p className="daily-memo-empty">{t("noStudents")}</p>}
                <StudentPagination {...pagination} label="memos" disabled={busy || readOnly} />
              </div>
            </section>
          );
        })}
      </div>
      {!memos.length && <p className="daily-memo-empty">{t("empty")}</p>}
      <form
        className="routine-add daily-memo-add"
        onSubmit={(event) => {
          event.preventDefault();
          void save({ type: "create", id: `memo_${crypto.randomUUID()}`, title });
        }}
      >
        <label>
          {t("add")}
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("placeholder")}
            maxLength={60}
            disabled={busy || readOnly}
          />
        </label>
        <button type="submit" disabled={busy || readOnly || !title.trim()}>
          {t("add")}
        </button>
      </form>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
